import { useState, useRef } from 'react';
import axios from 'axios';
import { 
    API_BASE_URL,
    UPLOAD_API_URL, 
    SINGLE_UPLOAD_LIMIT, 
    CHUNK_SIZE, 
    VALID_MIME_TYPES,
    SUPPORTED_TYPES,
    VALID_FILENAME_PATTERN,
    getFileTypeCategory
} from '@/utils/products/constants';
import { getFilenameValidationMessage } from '@/utils/products/mediaHelpers';

export const useProductMedia = (product, setProduct, showNotification, t = null) => {
    const fileInputRef = useRef(null);
    
    // Media upload state
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    
    // Track selected files and their upload status
    const [selectedFiles, setSelectedFiles] = useState([]);
    
    // Handle file selection for upload
    const handleFileSelect = () => {
        fileInputRef.current.click();
    };

    // Handle file selection change to preview files before upload
    const handleFileChange = (event) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;
        
        let invalidFileNames = 0;
        
        // Create file objects with initial state
        const newSelectedFiles = Array.from(files).map(file => ({
            file,
            id: `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            progress: 0,
            status: 'pending', // pending, uploading, completed, error
            error: null,
            result: null
        }));
        
        // Filter files with validation
        const filteredFiles = newSelectedFiles.filter(fileObj => {
            // 1. Validate file type
            if (!VALID_MIME_TYPES.includes(fileObj.file.type)) {
                const typeCategory = fileObj.file.type.split('/')[0];
                let message = `Unsupported file type: ${fileObj.file.type}`;
                
                // Give more specific guidance based on file category
                if (typeCategory === 'image') {
                    message += `. Supported file types: ${SUPPORTED_TYPES.IMAGE.join(', ')}`;
                } else if (typeCategory === 'audio') {
                    message += `. Supported file types: ${SUPPORTED_TYPES.AUDIO.join(', ')}`;
                } else if (typeCategory === 'video') {
                    message += `. Supported file types: ${SUPPORTED_TYPES.VIDEO.join(', ')}`;
                } else if (typeCategory === 'application' || typeCategory === 'text') {
                    message += `. See documentation for supported file types`;
                }
                
                showNotification(message, "warning");
                return false;
            }
            
            // 2. Validate filename format
            if (!VALID_FILENAME_PATTERN.test(fileObj.file.name)) {
                invalidFileNames++;
                
                // Get specific validation message with details on what's wrong
                const validationMessage = getFilenameValidationMessage(fileObj.file.name, t);
                
                // Add error information to the file object instead of immediately filtering it out
                fileObj.status = 'error';
                fileObj.error = validationMessage || 'Invalid filename';
                fileObj.invalidFilename = true;
                
                return false;
            }
            
            return true;
        });
        
        // Show general notification if any invalid filenames were found
        if (invalidFileNames > 0) {
            showNotification(
                typeof t === 'function' 
                    ? t('product.detail.invalidFilenamesError', {count: invalidFileNames})
                    : `${invalidFileNames} file(s) have invalid names. Only English letters, numbers, hyphens (-) and underscores (_) are allowed.`, 
                "error"
            );
        }
        
        setSelectedFiles(filteredFiles);
    };
    
    // Handle file upload with EmboxAPI
    const handleFileUpload = async () => {
        if (selectedFiles.length === 0) return;
        
        setUploading(true);
        
        try {
            const uploadedMedias = [];
            
            // Update all files to uploading status
            setSelectedFiles(prev => prev.map(fileObj => ({
                ...fileObj,
                status: 'uploading'
            })));
            
            for (let i = 0; i < selectedFiles.length; i++) {
                const fileObj = selectedFiles[i];
                const file = fileObj.file;
                
                try {
                    // Update status to show which file is currently uploading
                    setSelectedFiles(prev => prev.map(f => 
                        f.id === fileObj.id 
                            ? { ...f, status: 'uploading' } 
                            : f
                    ));
                    
                    let uploadResult;
                    
                    // Choose upload method based on file size
                    if (file.size <= SINGLE_UPLOAD_LIMIT) {
                        uploadResult = await uploadSingleFile(file, (progress) => {
                            // Update progress for this specific file
                            setSelectedFiles(prev => prev.map(f => 
                                f.id === fileObj.id 
                                    ? { ...f, progress } 
                                    : f
                            ));
                        });
                    } else {
                        uploadResult = await uploadMultipartFile(file, (progress) => {
                            // Update progress for this specific file
                            setSelectedFiles(prev => prev.map(f => 
                                f.id === fileObj.id 
                                    ? { ...f, progress } 
                                    : f
                            ));
                        });
                    }
                    
                    if (uploadResult.success) {
                        // Update file status to completed
                        setSelectedFiles(prev => prev.map(f => 
                            f.id === fileObj.id 
                                ? { 
                                    ...f, 
                                    status: 'completed', 
                                    progress: 100,
                                    result: uploadResult.data
                                } 
                                : f
                        ));
                        
                        uploadedMedias.push({
                            id: `media_${Date.now()}_${i}`,
                            url: uploadResult.data.data.accessUrl,
                            filename: file.name,
                            type: file.type.split('/')[0],
                            filesize: file.size,
                        });
                    } else {
                        // Update file status to error
                        setSelectedFiles(prev => prev.map(f => 
                            f.id === fileObj.id 
                                ? { 
                                    ...f, 
                                    status: 'error', 
                                    error: uploadResult.error 
                                } 
                                : f
                        ));
                    }
                } catch (fileError) {
                    // Handle individual file error
                    setSelectedFiles(prev => prev.map(f => 
                        f.id === fileObj.id 
                            ? { 
                                ...f, 
                                status: 'error', 
                                error: fileError.message || 'Upload failed' 
                            } 
                            : f
                    ));
                }
            }
            
            // Add successfully uploaded media to the product
            if (uploadedMedias.length > 0) {
                // Don't update state immediately with temp IDs anymore
                // Save each media to the database by calling the API
                try {
                    const savedMedias = [];
                    
                    for (let i = 0; i < uploadedMedias.length; i++) {
                        const media = uploadedMedias[i];
                        const newSortOrder = product.medias.length + i;
                        
                        // Call API to save media with productId
                        const response = await axios.post(`${API_BASE_URL}/product-media/create`, {
                            productId: product.productId,
                            mediaUrl: media.url,
                            mediaType: media.type,
                            mediaSize: media.filesize,
                            sortOrder: newSortOrder
                        });
                        
                        if (response.data.isSuccess) {
                            // Add the saved media with the real ID from the server
                            savedMedias.push({
                                id: response.data.data.id,
                                url: response.data.data.media_url,
                                filename: media.filename,
                                type: response.data.data.media_type,
                                filesize: response.data.data.media_size,
                                sortOrder: response.data.data.sort_order,
                            });
                        }
                    }
                    
                    // Update state with the saved medias that have real IDs
                    if (savedMedias.length > 0) {
                        setProduct(prev => ({
                            ...prev,
                            medias: [...prev.medias, ...savedMedias]
                        }));
                        
                        showNotification(`${savedMedias.length} file(s) uploaded successfully`, "success");
                    }
                    
                    // Clear the files that were uploaded successfully
                    setSelectedFiles(prev => prev.filter(f => f.status !== 'completed'));
                } catch (saveError) {
                    console.error("Error saving media to product:", saveError);
                    showNotification("Files were uploaded but couldn't be saved to the product: " + 
                        (saveError.message || "Unknown error"), "warning");
                }
            }
            
        } catch (error) {
            console.error("Error uploading files:", error);
            showNotification("Failed to upload files: " + (error.message || "Unknown error"), "error");
        } finally {
            setUploading(false);
            
            // Clear the file input
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };
    
    // Single upload for files ≤ 5MB
    const uploadSingleFile = async (file, progressCallback) => {
        try {
            // Read file as ArrayBuffer
            const fileData = await file.arrayBuffer();
            
            // Create blob with correct content type
            const blob = new Blob([fileData], { type: file.type });
            
            // Create form data
            const formData = new FormData();
            formData.append('fileName', file.name);
            formData.append('contentType', file.type);
            formData.append('fileSize', file.size.toString());
            formData.append('body', blob);
            
            // Upload file with progress tracking
            const response = await axios.post(UPLOAD_API_URL, formData, {
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    progressCallback(progress);
                }
            });
            
            return { success: true, data: response.data };
        } catch (error) {
            console.error("Single upload error:", error);
            return { 
                success: false, 
                error: error.response?.data?.message || error.message 
            };
        }
    };
    
    // Multipart upload for files > 5MB
    const uploadMultipartFile = async (file, progressCallback) => {
        try {
            // 1. Initialize upload
            const initiateResponse = await axios.post(`${UPLOAD_API_URL}/multipart`, {
                action: 'initiate',
                fileName: file.name,
                contentType: file.type,
                fileSize: file.size
            });
            
            if (!initiateResponse.data.isSuccess) {
                throw new Error(initiateResponse.data.message || 'Could not initiate upload');
            }
            
            const { uploadId, key } = initiateResponse.data;
            
            // 2. Upload each part
            const parts = [];
            const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
            
            for (let partNumber = 1; partNumber <= totalChunks; partNumber++) {
                const start = (partNumber - 1) * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, file.size);
                const chunk = file.slice(start, end);
                
                const url = new URL(`${UPLOAD_API_URL}/part`);
                url.searchParams.append('uploadId', uploadId);
                url.searchParams.append('partNumber', partNumber.toString());
                url.searchParams.append('key', key);
                
                const partResponse = await axios.post(url, chunk, {
                    headers: {
                        'Content-Type': 'application/octet-stream'
                    },
                    onUploadProgress: (progressEvent) => {
                        // Calculate overall progress based on current part
                        const partProgress = progressEvent.loaded / progressEvent.total;
                        const overallProgress = ((partNumber - 1) + partProgress) / totalChunks;
                        const progress = Math.round(overallProgress * 100);
                        progressCallback(progress);
                    }
                });
                
                if (!partResponse.data.isSuccess) {
                    throw new Error(partResponse.data.message || 'Could not upload part');
                }
                
                parts.push(partResponse.data.data);
            }
            
            // 3. Complete the upload
            const completeResponse = await axios.post(`${UPLOAD_API_URL}/multipart`, {
                action: 'complete',
                key,
                uploadId,
                parts
            });
            
            if (!completeResponse.data.isSuccess) {
                throw new Error(completeResponse.data.message || 'Could not complete upload');
            }
            
            return { success: true, data: completeResponse.data };
        } catch (error) {
            console.error("Multipart upload error:", error);
            return { 
                success: false, 
                error: error.response?.data?.message || error.message 
            };
        }
    };
    
    // Handle removing a media item
    const handleRemoveMedia = async (mediaId, onComplete = null) => {
        try {
            console.log("handleRemoveMedia called with ID:", mediaId);
            
            const media = product.medias.find(m => m.id === mediaId);
            if (!media) {
                showNotification('Media not found', "error");
                console.log("Media not found in local state");
                if (onComplete) onComplete(false);
                return;
            }
            
            // Show processing notification
            showNotification('Removing media...', "info");
            
            // Extract filename from media URL
            const url = new URL(media.url);
            const pathname = url.pathname;
            const filename = pathname.substring(pathname.lastIndexOf('/') + 1);
            
            // Step 1: Delete file from R2 storage
            try {
                const deleteFileResponse = await axios.delete(`${UPLOAD_API_URL}/delete/${filename}`);
                if (!deleteFileResponse.data.isSuccess) {
                    throw new Error(deleteFileResponse.data.message || "Failed to delete file from storage");
                }
                console.log("File deleted from storage successfully");
            } catch (fileDeleteError) {
                console.error("Error deleting file from storage:", fileDeleteError);
                showNotification(fileDeleteError.message || "Failed to delete file from storage", "error");
                if (onComplete) onComplete(false);
                return; // Stop if file deletion fails
            }
            
            // Step 2: Delete media from product
            const response = await axios.post(`${API_BASE_URL}/product-media/${mediaId}/delete`);
            
            if (response.data.isSuccess) {
                // Update local state
                setProduct(prev => ({
                    ...prev,
                    medias: prev.medias.filter(media => media.id !== mediaId)
                }));
                
                // Remove this media from any variants that use it
                setProduct(prev => ({
                    ...prev,
                    variants: prev.variants.map(variant => {
                        const updatedVariant = { ...variant };
                        
                        // Remove from mediaIds array if present
                        if (updatedVariant.mediaIds && updatedVariant.mediaIds.includes(mediaId)) {
                            updatedVariant.mediaIds = updatedVariant.mediaIds.filter(id => id !== mediaId);
                        }
                        
                        // Clear main mediaId if it matches
                        if (updatedVariant.mediaId === mediaId) {
                            updatedVariant.mediaId = updatedVariant.mediaIds && updatedVariant.mediaIds.length > 0 
                                ? updatedVariant.mediaIds[0] 
                                : '';
                        }
                        
                        return updatedVariant;
                    })
                }));
                
                showNotification('Media removed successfully', "success");
                console.log("Media removed successfully, calling onComplete with true");
                if (onComplete) onComplete(true);
            } else {
                throw new Error(response.data.message || "Failed to delete media");
            }
        } catch (error) {
            console.error("Error removing media:", error);
            showNotification(error.message || "Failed to remove media", "error");
            if (onComplete) onComplete(false);
        }
    };
    
    // Cancel upload for a specific file
    const handleCancelUpload = (fileId) => {
        setSelectedFiles(prev => prev.filter(f => f.id !== fileId));
    };
    
    // Clear all selected files
    const handleClearSelectedFiles = () => {
        if (!uploading) {
            setSelectedFiles([]);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };
    
    return {
        fileInputRef,
        selectedFiles,
        uploading,
        uploadProgress,
        handleFileSelect,
        handleFileChange,
        handleFileUpload,
        handleRemoveMedia,
        handleCancelUpload,
        handleClearSelectedFiles
    };
};